import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoginStatus } from '../shared/login-status-service';
import { AutoLogoutService } from '../shared/auto-logout.service';
import { HubService, isProvisionedEndpoint } from '../../home/hub.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// fa-zz-auth tokens use standard OIDC claims (sub, email, given_name, family_name)
// plus namespaced custom claims for product-specific values. The namespace mirrors
// the Auth0 custom-claim convention so the eventual Auth0 cutover is a config change.
const CLAIM_NAMESPACE = 'https://zeidman.info/';

interface JwtPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  [namespaced: string]: unknown;
}

@Component({
  selector: 'audit-sso',
  standalone: true,
  imports: [MatProgressSpinnerModule],
  template: `
    <div class="sso-container zd-gradient">
      <mat-spinner diameter="48"></mat-spinner>
      <p class="sso-message">Signing you in...</p>
    </div>
  `,
  styles: [`
    .sso-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      color: white;
    }
    .sso-message {
      margin-top: 1rem;
      font-size: 1.1rem;
    }
  `]
})
export class SsoComponent implements OnInit {

  route = inject(ActivatedRoute);
  router = inject(Router);
  loginStatus = inject(LoginStatus);
  autoLogout = inject(AutoLogoutService)
  hub = inject(HubService);

  ngOnInit(): void {
    // Senders pass tokens in the URL fragment (#access=...&refresh=...) so the
    // refresh token never reaches server access logs. We still accept the legacy
    // `?token=...` query param for backwards compatibility with stale tabs.
    const { accessToken, refreshToken } = readSsoCredentials({
      hash: window.location.hash,
      search: window.location.search,
    });

    // Wipe the fragment from the address bar + browser history immediately.
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (!accessToken) {
      this.router.navigate(['/login']);
      return;
    }

    const payload = this.decodeJwt(accessToken);

    if (!payload || this.isExpired(payload)) {
      this.router.navigate(['/login']);
      return;
    }

    // Populate sessionStorage with the same keys the normal login flow uses.
    // Claim shape:
    //   sub                               → user id
    //   email                             → email
    //   given_name / family_name          → first / last name
    //   https://zeidman.info/orgId        → tenant id
    //   https://zeidman.info/orgName      → tenant name
    sessionStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      sessionStorage.setItem('refreshToken', refreshToken);
    } else {
      sessionStorage.removeItem('refreshToken');
    }
    sessionStorage.setItem('userName', (payload.email ?? '') as string);
    sessionStorage.setItem('firstName', (payload.given_name ?? '') as string);
    sessionStorage.setItem('lastName', (payload.family_name ?? '') as string);
    sessionStorage.setItem('orgId', String(payload[CLAIM_NAMESPACE + 'orgId'] ?? ''));
    sessionStorage.setItem('orgName', String(payload[CLAIM_NAMESPACE + 'orgName'] ?? ''));
    sessionStorage.setItem('locale', 'en-US');

    this.loginStatus.setStatus(true);

    // Replicate the post-login flow: fetch OrgStack URL and org details.
    // getOrgDetails is best-effort (envId/envName are display-only); failing
    // it must not bounce the user out of an otherwise-good session. Only an
    // OrgStack lookup failure is fatal — without it the app cannot reach
    // Process Core.
    forkJoin([
      this.hub.getOrgStackURL(),
      this.hub.getOrgDetails().pipe(catchError(() => of(null)))
    ]).subscribe({
      next: ([endpoint, details]) => {
        // A missing org is reported by ZDHub as a successful response
        // carrying a {success:false} envelope, so it arrives here rather
        // than in the error branch below. Treat it the same way: without an
        // endpoint the app cannot reach Process Core, and a half-populated
        // session is worse than none.
        if (!isProvisionedEndpoint(endpoint)) {
          this.loginStatus.logout();
          this.router.navigate(['/login']);
          return;
        }
        if (details) {
          sessionStorage.setItem('envId', details.environment_id);
          sessionStorage.setItem('envName', details.environment_name);
        }
        this.autoLogout.init();
        this.router.navigate(['/dashboard']);
      },
      error: () => {
        // OrgStack not configured — config hasn't been run. Clear the
        // session we just populated so the toolbar doesn't show a
        // half-logged-in state behind the login form.
        this.loginStatus.logout();
        this.router.navigate(['/login']);
      }
    });
  }

  private decodeJwt(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      // Base64url decode
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  private isExpired(payload: JwtPayload): boolean {
    if (!payload.exp) return false;
    return Date.now() >= (payload.exp as number) * 1000;
  }
}

/**
 * Pulls SSO credentials from the URL fragment (preferred) or the legacy query
 * string. Exported for unit tests; the component is the only runtime caller.
 */
export function readSsoCredentials(loc: { hash: string; search: string }): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  const hash = (loc.hash ?? '').replace(/^#/, '');
  if (hash) {
    const params = new URLSearchParams(hash);
    const access = params.get('access');
    if (access) {
      return { accessToken: access, refreshToken: params.get('refresh') };
    }
  }
  const search = new URLSearchParams((loc.search ?? '').replace(/^\?/, ''));
  return { accessToken: search.get('token'), refreshToken: null };
}
